import { useAtom, useSetAtom } from "jotai";
import {
	BotIcon,
	LogOutIcon,
	MonitorIcon,
	MoonIcon,
	PlusIcon,
	RefreshCwIcon,
	SparklesIcon,
	SunIcon,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/auth-provider";
import { useOpenAssistant } from "@/components/assistant/store";
import { ProjectIcon } from "@/components/projects/project-icon";
import {
	projectSettingsPath,
	projectSettingsTabs,
} from "@/components/settings/project-settings-tabs";
import { useSyncContentNow } from "@/components/settings/sync-now";
import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Shortcut } from "@/components/ui/kbd";
import { useCurrentProject } from "@/hooks/use-current-project";
import { useCan } from "@/hooks/use-permissions";
import {
	type ProjectNavItem,
	projectNavigation,
	projectNavSections,
	projectPath,
	projectSettingsNavItem,
	workspaceNavigation,
} from "@/routes/app-navigation";
import { commandPaletteOpenAtom, projectDialogAtom } from "@/store/ui";
import { useRecords } from "@/sync/hooks";
import type { Project } from "@/sync/models/project";

const isActive = (project: Project) => !project.archivedAt;
const unsectioned = projectNavigation.filter((item) => !item.section);

/** ⌘K. Searches the local object pool — no network, so it is instant. */
export function CommandPalette() {
	const [open, setOpen] = useAtom(commandPaletteOpenAtom);
	const openProjectDialog = useSetAtom(projectDialogAtom);
	const navigate = useNavigate();
	const { setTheme } = useTheme();
	const { logout } = useAuth();
	const { projectId, project } = useCurrentProject();
	const projects = useRecords("project", { filter: isActive });
	const openAssistant = useOpenAssistant();
	const canSync = useCan("social.manage");
	const { sync } = useSyncContentNow(projectId);

	const run = useCallback(
		(action: () => void) => {
			setOpen(false);
			action();
		},
		[setOpen],
	);

	const goItem = (item: ProjectNavItem, id: string) => (
		<CommandItem
			key={item.id}
			value={`go ${item.label}`}
			onSelect={() => run(() => navigate(projectPath(id, item.segment)))}
		>
			<item.icon />
			{item.label}
			<Shortcut keys={item.shortcut} />
		</CommandItem>
	);

	return (
		<CommandDialog open={open} onOpenChange={setOpen}>
			<CommandInput placeholder="Type a command or search…" />
			<CommandList>
				<CommandEmpty>No results.</CommandEmpty>
				<CommandGroup heading="Assistant">
					<CommandItem value="ask assistant ai chat" onSelect={() => run(() => openAssistant())}>
						<SparklesIcon />
						Ask the assistant
						<Shortcut keys="mod+j" />
					</CommandItem>
				</CommandGroup>
				{projectId && project && (
					<>
						<CommandGroup heading={project.name}>
							{canSync && (
								<CommandItem
									value="sync content now refresh metrics comments import"
									onSelect={() => run(() => void sync())}
								>
									<RefreshCwIcon />
									Sync content now
								</CommandItem>
							)}
							<CommandItem
								value="new agent create automate"
								onSelect={() => run(() => navigate(`${projectPath(projectId, "agents")}?new=1`))}
							>
								<BotIcon />
								New agent
							</CommandItem>
						</CommandGroup>
						{projectNavSections.map((section) => (
							<CommandGroup key={section.id} heading={`Go to · ${section.label}`}>
								{projectNavigation
									.filter((item) => item.section === section.id)
									.map((item) => goItem(item, projectId))}
							</CommandGroup>
						))}
						<CommandGroup heading="Go to · Project">
							{unsectioned.map((item) => goItem(item, projectId))}
							{goItem(projectSettingsNavItem, projectId)}
							{projectSettingsTabs.map((tab) => (
								<CommandItem
									key={`settings-${tab.id}`}
									value={`go settings ${tab.label}`}
									onSelect={() => run(() => navigate(projectSettingsPath(projectId, tab.id)))}
								>
									<projectSettingsNavItem.icon />
									Settings · {tab.label}
								</CommandItem>
							))}
						</CommandGroup>
					</>
				)}
				<CommandGroup heading="Projects">
					{projects.map((p) => (
						<CommandItem
							key={p.id}
							value={`project ${p.name} ${p.id}`}
							onSelect={() => run(() => navigate(projectPath(p.id, "overview")))}
						>
							<ProjectIcon name={p.name} />
							{p.name}
						</CommandItem>
					))}
					<CommandItem
						value="new project create"
						onSelect={() => run(() => openProjectDialog({ mode: "create" }))}
					>
						<PlusIcon />
						New project
						<Shortcut keys="c" />
					</CommandItem>
				</CommandGroup>
				<CommandGroup heading="Workspace">
					{workspaceNavigation.map((item) => (
						<CommandItem
							key={item.id}
							value={`go ${item.label}`}
							onSelect={() => run(() => navigate(item.path))}
						>
							<item.icon />
							{item.label}
							<Shortcut keys={item.shortcut} />
						</CommandItem>
					))}
				</CommandGroup>
				<CommandGroup heading="Preferences">
					<CommandItem value="theme light" onSelect={() => run(() => setTheme("light"))}>
						<SunIcon />
						Light theme
					</CommandItem>
					<CommandItem value="theme dark" onSelect={() => run(() => setTheme("dark"))}>
						<MoonIcon />
						Dark theme
					</CommandItem>
					<CommandItem value="theme system" onSelect={() => run(() => setTheme("system"))}>
						<MonitorIcon />
						System theme
					</CommandItem>
					<CommandItem value="log out sign out" onSelect={() => run(() => void logout())}>
						<LogOutIcon />
						Log out
					</CommandItem>
				</CommandGroup>
			</CommandList>
		</CommandDialog>
	);
}
